/* eslint-disable no-shadow */
import { END, buffers, eventChannel } from "redux-saga";
import {
    call,
    cancel,
    cancelled,
    delay,
    flush,
    fork,
    race,
    take,
    takeEvery,
} from "redux-saga/effects";
import noop from "lodash.noop";
import { ONE_SECOND } from "Globals/constants/constants";

import {
    LOAD_REQUESTED,
    LOAD_SINGLE_REQUESTED,
    SUBSCRIBE,
    UNSUBSCRIBE,
    UPDATE_READ_REQUESTED,
    defaultScopeId,
    failTaskLoad,
    failTasksLoad,
    failTasksUpdateRead,
    requestTasksLoad,
    requestTasksUpdateRead,
    succeedTaskLoad,
    succeedTasksLoad, 
    succeedTasksUpdateRead
} from "./workflowTasks.duck";

import {
    loadSingleTask,
    loadTasks,
    subscribeTasks,
    updateManyTasks
} from "./workflowTasks.api";

import { getId, injectBoundActionCreatorsFactory, withRefreshRequest } from "Utils/sagaUtils";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failTaskLoad,
        failTasksLoad,
        failTasksUpdateRead,
        requestTasksLoad,
        requestTasksUpdateRead,
        succeedTaskLoad,
        succeedTasksLoad,
        succeedTasksUpdateRead
    },
    getId,
    defaultScopeId
);

export function* loadSingleTaskSaga({
    data,
    actions: { succeedTaskLoad, failTaskLoad },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield call(loadSingleTask, data);
        yield succeedTaskLoad(result);
        yield call(resolve);
    } catch (e) {
        yield failTaskLoad(e);
        yield call(reject, e);
    }
}

export function* loadTasksSaga({
    data,
    actions: { succeedTasksLoad, failTasksLoad },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield call(loadTasks, data);
        yield succeedTasksLoad(result);
        yield call(resolve);
    } catch (e) {
        yield failTasksLoad(e);
        yield call(reject, e);
    }
}

export function* updateReadTasksSaga({
    data,
    actions: { succeedTasksUpdateRead, failTasksUpdateRead },
    resolve = noop,
    reject = noop,
}) {
    try {
        const updateTasks = data.map(id => ({ id, read: true }));
        yield delay(ONE_SECOND * 5);
        const result = yield call(updateManyTasks, updateTasks);
        yield succeedTasksUpdateRead(result);
        yield call(resolve);
    } catch (e) {
        yield failTasksUpdateRead({ error: e, tasks: data });
        yield call(reject, e);
    }
}

export const tasksSubscription = subject => {
    return eventChannel(
        async emitter => {
            try {
                const sub = await subscribeTasks(subject, emitter);
                return sub;
            } catch (e) {
                emitter(END);
            }
        },
        buffers.expanding(1) // buffer one invocation holding the most recent event
    );
};

export function* subscribeToTasks({
    subject,
    actions: { succeedTasksLoad },
}) {
    const chan = yield call(tasksSubscription, subject);
    try {
        while (true) {
            const tasks = yield flush(chan);
            if (tasks.length > 0) {
                const taskItems = tasks.flat();
                yield succeedTasksLoad(taskItems);
            }
            yield delay(ONE_SECOND);
        }
    } finally {
        if (yield cancelled()) {
            chan.close();
        }
    }
}

export function* subscribeTasksSaga() {
    const subscriptions = {};
    const tasks = {};
    while (true) {
        const { subscribe, unsubscribe } = yield race({
            subscribe: take(SUBSCRIBE),
            unsubscribe: take(UNSUBSCRIBE),
        });
        const {
            data: { userId, taskId },
        } = subscribe || unsubscribe;
        const ids = [ userId, taskId && `id-${taskId}` ].filter(i => i);
        if (subscribe) {
            for (const subId of ids) {
                subscriptions[subId] = subscriptions[subId] || 0;
                subscriptions[subId]++;
                if (subscriptions[subId] === 1) {
                    tasks[subId] = yield fork(
                        withBoundActions(subscribeToTasks),
                        { ...(subscribe || unsubscribe), subject: subId }
                    );
                }
            }
        }
        if (unsubscribe) {
            for (const subId of ids) {
                subscriptions[subId]--;
                if (subscriptions[subId] === 0) {
                    yield cancel(tasks[subId]);
                }
            }
        }
    }
}

export default () =>
    function* rootSaga() {
        yield takeEvery(
            LOAD_REQUESTED,
            withBoundActions(loadTasksSaga)
        );
        yield takeEvery(
            UPDATE_READ_REQUESTED,
            withBoundActions(updateReadTasksSaga)
        );
        yield takeEvery(
            LOAD_SINGLE_REQUESTED,
            withRefreshRequest(withBoundActions(loadSingleTaskSaga))
        );
        yield fork(subscribeTasksSaga);
    };
